import { useEffect, type ReactNode } from "react"
import { useDispatch } from "react-redux"

import { useIsScoped } from "../../hooks/useEntityScope"
import { scopeCleared } from "../../slice/entityScopeSlice"

/**
 * Un-chooses the account for the internal console's scope-agnostic routes: the
 * Global Orders asset workspace (DV-2849, PR #1159 review) and the account
 * detail pages (DV-3364).
 *
 * The console is a pre-selection surface: every other `/internal/*` row (all
 * but those two) is `entity: 'none'`, which `PrivateRoute` reads as the
 * assertion "no org is selected". The asset workspace is `entity: 'any'` on
 * purpose (spec D3), so a copied link opens in a tab that already has an
 * account — but that left the tab sitting inside console chrome while still
 * scoped, and three things break there:
 *
 *   - Back, and the not-found / load-error heroes, navigate to
 *     `/internal/global-orders`, which `PrivateRoute` then bounces to `/`
 *     (`storeIntendedUrl()` stores only `/home/*` URLs, so the deep link is
 *     lost on the way out too). `AdminShell`'s four sidebar items fail
 *     identically — they are client-side navigations into the same `'none'`
 *     rows.
 *   - Every request carries `X-Entity-UUID` naming the SESSION's account while
 *     the page reads and writes a property belonging to another one — the
 *     exact header-vs-UI drift `entity-scope-header.ts` exists to rule out.
 *     Inert under today's `Mode.OFF`, but it would record as cross-entity
 *     access in DV-3065's log-only pass and 403 under DV-3066.
 *   - `getOrgSelectedAllData` branches on scope, so the workspace behaved one
 *     way when opened from the console and another way from a scoped tab. (Its
 *     PMS reads no longer do: since DV-3345 they name the property's account
 *     whatever the scope.)
 *
 * Clearing on entry collapses all of them: the route has one behavior, and it is
 * the unscoped one its chrome already claims (internal sidebar, no entity
 * tree). `scopeCleared` is what "Switch Organization" dispatches — it
 * un-chooses without touching the token claim — so the cost is the account
 * selection and nothing else. For the PROFILE, not just this tab: it is
 * persisted, and `selectedOrg` is localStorage, so another open tab keeps its
 * account in memory and lands on the picker at its next reload, exactly as
 * after Switch Organization. Accepted; a tab-local "suspend" was built and
 * discarded (spec §3.2, 2026-09-22 correction) — don't reach for it here.
 *
 * `scopeCleared` leaves `hasWatermark` behind; `selectHasWatermarkAvailable`
 * reads it as unknown while unscoped. The workspace mounted here does not
 * read it at all: its Download and Send gates follow the property's own
 * account (`useWorkspaceAccount`, DV-2849 PR 2).
 *
 * Children are withheld for the one commit this takes, rather than rendered
 * and then corrected, so no request can leave while the header would still
 * name the old account.
 *
 * It clears on EVERY change to scope, not just on mount: an account chosen
 * while the page stays put is cleared too, which is what keeps the tab out of
 * console chrome while scoped. The cost is an ordering dependency for Log In
 * As from the account detail page (DV-3364): it works only because
 * `switchEntity`'s `scopeChanged` and its navigation away render in one pass,
 * so this effect never sees the new scope here. The navigation port makes
 * that pass one with `flushSync` (`useNavigationPort`); without it a browser
 * renders the scope first, and this cleared the account the admin had just
 * logged in as. An `await` between the dispatch and the landing would undo it.
 * A mount-only clear would remove the dependency and reopen the
 * scoped-in-chrome bug. The tripwire is the Log In As test in
 * `e2e/routes/account-detail.spec.ts`; jsdom renders the two together either
 * way, so `AccountDetailLoginAs.integration.test.tsx` cannot see it.
 */
export function UnscopedConsole({ children }: { children: ReactNode }) {
  const isScoped = useIsScoped()
  const dispatch = useDispatch()

  useEffect(() => {
    if (isScoped) dispatch(scopeCleared())
  }, [isScoped, dispatch])

  if (isScoped) return null
  return children
}

export default UnscopedConsole
